import { api } from './api';

// Types
export type TargetPeriodType = 'MONTHLY' | 'CUSTOM';

export interface BranchTarget {
  id: string;
  branchId: string;
  branchName: string;
  branchCode: string;
  periodType: TargetPeriodType;
  month: number | null;
  year: number;
  startDate: string | null;
  endDate: string | null;
  // Revenue targets (kept for backward compat, hidden in UI)
  serviceTarget: number;
  invoiceTarget: number;
  serviceAchieved: number;
  invoiceAchieved: number;
  servicePercentage: number;
  invoicePercentage: number;
  totalTarget: number;
  totalAchieved: number;
  totalPercentage: number;
  // Service count targets (NEW - primary metric)
  serviceCountTarget: number;
  serviceCountCompleted: number;
  serviceCountPending: number;
  serviceCountPercentage: number;
  notes: string | null;
  createdAt: string;
}

export interface CreateTargetInput {
  branchId: string;
  periodType: TargetPeriodType;
  month?: number;
  year: number;
  startDate?: string;
  endDate?: string;
  serviceTarget?: number;
  invoiceTarget?: number;
  serviceCountTarget: number; // Primary metric
  notes?: string;
}

export interface UpdateTargetInput {
  serviceTarget?: number;
  invoiceTarget?: number;
  serviceCountTarget?: number; // Primary metric
  notes?: string;
}

export interface TargetFilters {
  branchId?: string;
  year?: number;
  month?: number;
  page?: number;
  limit?: number;
}

export interface BranchSummary {
  branchId: string;
  branchName: string;
  branchCode: string;
  hasTarget: boolean;
  // Revenue targets (backward compat, hidden in UI)
  serviceTarget: number;
  invoiceTarget: number;
  serviceAchieved: number;
  invoiceAchieved: number;
  servicePercentage: number;
  invoicePercentage: number;
  // Service count targets (NEW - primary metric)
  serviceCountTarget: number;
  serviceCountCompleted: number;
  serviceCountPending: number;
  serviceCountPercentage: number;
}

export interface DashboardSummary {
  period: {
    year: number;
    month: number;
  };
  branches: BranchSummary[];
  totals: {
    // Revenue (backward compat)
    serviceTarget: number;
    invoiceTarget: number;
    serviceAchieved: number;
    invoiceAchieved: number;
    servicePercentage: number;
    invoicePercentage: number;
    // Service count (NEW - primary)
    serviceCountTarget: number;
    serviceCountCompleted: number;
    serviceCountPending: number;
    serviceCountPercentage: number;
  };
}

export interface TargetListResponse {
  success: boolean;
  targets: BranchTarget[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

// Helper functions
export const getMonthName = (month: number): string => {
  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
  return months[month - 1] || '';
};

export const getStatusColor = (percentage: number): string => {
  if (percentage >= 100) return 'bg-green-100 text-green-700 border-green-200';
  if (percentage >= 75) return 'bg-yellow-100 text-yellow-700 border-yellow-200';
  if (percentage >= 50) return 'bg-orange-100 text-orange-700 border-orange-200';
  return 'bg-red-100 text-red-700 border-red-200';
};

export const getStatusLabel = (percentage: number): string => {
  if (percentage >= 100) return 'Achieved';
  if (percentage >= 75) return 'On Track';
  if (percentage >= 50) return 'Behind';
  return 'Critical';
};

export const getProgressBarColor = (percentage: number): string => {
  if (percentage >= 100) return 'bg-green-500';
  if (percentage >= 75) return 'bg-yellow-500';
  if (percentage >= 50) return 'bg-orange-500';
  return 'bg-red-500';
};

// API methods
export const branchTargetApi = {
  // Create a new target
  createTarget: async (data: CreateTargetInput): Promise<BranchTarget> => {
    const response = await api.post('/branch-targets', data);
    return response.data.data;
  },

  // Update a target
  updateTarget: async (id: string, data: UpdateTargetInput): Promise<BranchTarget> => {
    const response = await api.put(`/branch-targets/${id}`, data);
    return response.data.data;
  },

  // Delete a target
  deleteTarget: async (id: string): Promise<void> => {
    await api.delete(`/branch-targets/${id}`);
  },

  // Get a target by ID
  getTargetById: async (id: string): Promise<BranchTarget> => {
    const response = await api.get(`/branch-targets/${id}`);
    return response.data.data.target;
  },

  // Get all targets with filters
  getTargets: async (filters: TargetFilters = {}) => {
    const params = new URLSearchParams();
    if (filters.branchId) params.append('branchId', filters.branchId);
    if (filters.year) params.append('year', filters.year.toString());
    if (filters.month) params.append('month', filters.month.toString());
    if (filters.page) params.append('page', filters.page.toString());
    if (filters.limit) params.append('limit', filters.limit.toString());

    const response = await api.get(`/branch-targets?${params.toString()}`);
    return response.data.data;
  },

  // Get dashboard summary
  getDashboardSummary: async (year?: number, month?: number): Promise<DashboardSummary> => {
    const params = new URLSearchParams();
    if (year) params.append('year', year.toString());
    if (month) params.append('month', month.toString());

    const response = await api.get(`/branch-targets/dashboard?${params.toString()}`);
    return response.data.data;
  },

  // Get targets for a specific branch
  getBranchTargets: async (branchId: string, year?: number) => {
    const params = new URLSearchParams();
    if (year) params.append('year', year.toString());

    const response = await api.get(`/branch-targets/branch/${branchId}?${params.toString()}`);
    return response.data.data;
  },

  // Get target split (today vs month) for a branch
  getTargetSplit: async (branchId: string) => {
    const response = await api.get(`/branch-targets/target-split/${branchId}`);
    return response.data.data;
  },
};
